import React, { FC, useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Button, Form, message, Modal, Select, Spin } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { ExportToCsv } from 'export-to-csv';
import { nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { CSV_EXPORT_DEFAULTS, ROLE_TYPES } from '../../utils/DataConstants';
import {
	getExcludedAssetTypes,
	updateExcludedAssetTypes,
} from '../../thunks/cycle_count_excluded_asset_types_thunks';
import {
	assetTypesRestCrudThunksForBuyer,
	assetTypesRestCrudThunksForSupplier,
	fetchAssetTypesLiteForBuyer,
	fetchAssetTypesLiteForSupplier,
} from '../../thunks/asset_types_thunks';
import UploadWithSampleModal from '../flatfile/UploadWithSampleModal';
import OWAsyncSelect from '../ow_async_select/OWAsyncSelect';
import { withUserType } from '../withUserType';

const EXCLUDED_ASSET_TYPES_TARGET_COLLECTION = 'excludedAssetTypesSelect';

const getExcludedAssetTypeFlatfileFields = (assetTypeOptions) => [
	{
		label: 'Asset Type',
		key: 'assetTypeId',
		type: 'enum',
		config: {
			options: assetTypeOptions,
		},
		constraints: [{ type: 'required' }, { type: 'unique' }],
	},
];

interface ExcludedAssetTypesModalProps {
	visible: boolean;
	onClose: () => void;
	userType: string;
	assetTypes: any;
	fetchExcludedAssetTypes: () => Promise<any>;
	saveExcludedAssetTypes: (data: any) => Promise<any>;
	fetchAssetTypesLite: (
		params: any,
		targetCollectionName?: any,
		pagination?: any,
		sorting?: any,
		filters?: any,
		addToTargetCollection?: boolean
	) => void;
	fetchAllAssetTypesForUpload: (
		params: any,
		targetCollectionName?: any,
		pagination?: any,
		sorting?: any,
		filters?: any,
		addToTargetCollection?: boolean
	) => Promise<any>;
}

const ExcludedAssetTypesModal: FC<ExcludedAssetTypesModalProps> = ({
	visible,
	onClose,
	assetTypes,
	fetchExcludedAssetTypes,
	saveExcludedAssetTypes,
	fetchAssetTypesLite,
	fetchAllAssetTypesForUpload,
}) => {
	const [form] = Form.useForm();
	const [uploadModalVisible, setUploadModalVisible] = useState(false);
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [downloading, setDownloading] = useState(false);
	const [assetTypeOptions, setAssetTypeOptions] = useState([]);
	const [fetchingAssetTypes, setFetchingAssetTypes] = useState(false);

	useEffect(() => {
		if (visible) {
			setLoading(true);
			fetchExcludedAssetTypes()
				.then((data) => {
					const ids = nullSafeGetOrElse('excludedAssetTypeIds', data, []);
					form.setFieldsValue({ excludedAssetTypeIds: ids });
				})
				.catch(() => {})
				.finally(() => setLoading(false));
		}
	}, [visible]); // eslint-disable-line react-hooks/exhaustive-deps

	const handleSave = useCallback(
		(values) => {
			const ids = values.excludedAssetTypeIds || [];
			setSaving(true);
			saveExcludedAssetTypes({ excludedAssetTypeIds: ids })
				.then(() => {
					message.success('Excluded asset types updated successfully.');
					onClose();
				})
				.catch(() => {
					message.error('Failed to update excluded asset types.');
				})
				.finally(() => {
					setSaving(false);
				});
		},
		[saveExcludedAssetTypes, onClose]
	);

	const handleUploadClick = useCallback(() => {
		setFetchingAssetTypes(true);
		fetchAllAssetTypesForUpload({}, null, { current: 1, pageSize: 10000 })
			.then((allAssetTypes) => {
				const options = (allAssetTypes || []).map((assetType) => ({
					label: nullSafeGetOrElse('name', assetType, ''),
					value: String(assetType.id),
				}));
				setAssetTypeOptions(options);
				setUploadModalVisible(true);
			})
			.finally(() => {
				setFetchingAssetTypes(false);
			});
	}, [fetchAllAssetTypesForUpload]);

	const handleDownload = useCallback(() => {
		setDownloading(true);
		try {
			const csvTitle = 'excluded_asset_types';
			const csvExporter = new ExportToCsv({
				...CSV_EXPORT_DEFAULTS,
				filename: csvTitle,
				title: csvTitle,
				headers: ['Asset Type'],
			});

			const ids = form.getFieldValue('excludedAssetTypeIds') || [];
			const records = nullSafeGetOrElse('records', assetTypes, {});
			const data =
				ids.length > 0
					? ids.map((id) => ({
							assetTypeId: nullSafeGetOrElse('name', records[id], ''),
					  }))
					: [{ assetTypeId: 'Example Asset Type' }];

			csvExporter.generateCsv(data);
		} finally {
			setDownloading(false);
		}
	}, [form, assetTypes]);

	const handleUpload = useCallback(
		(results) => {
			const validData = results.validData || [];
			const assetTypeIds = validData.map((record) => parseInt(record.assetTypeId, 10));

			return saveExcludedAssetTypes({ excludedAssetTypeIds: assetTypeIds })
				.then(() => {
					message.success('Excluded asset types updated successfully.');
					setUploadModalVisible(false);
					onClose();
				})
				.catch(() => {
					message.error('Failed to update excluded asset types.');
					setUploadModalVisible(false);
				});
		},
		[saveExcludedAssetTypes, onClose]
	);

	const handleUploadCancel = useCallback(() => {
		setUploadModalVisible(false);
	}, []);

	const currentIds = Form.useWatch('excludedAssetTypeIds', form) || [];

	return (
		<>
			<Modal
				visible={visible && !uploadModalVisible}
				title={
					<div className="flex flex-col gap-2">
						<div className="h4medium">{'Excluded Asset Types (' + currentIds.length + ')'}</div>
						<div className="bodyregular text-character-secondary">
							The following asset types will be excluded from all cycle count events
						</div>
					</div>
				}
				width={600}
				onCancel={onClose}
				destroyOnClose
				footer={
					<div className="flex items-center justify-between">
						<Button
							icon={<UploadOutlined />}
							loading={fetchingAssetTypes}
							onClick={handleUploadClick}
						>
							Upload
						</Button>
						<div>
							<Button className="mr-2" onClick={onClose}>
								Cancel
							</Button>
							<Button type="primary" loading={saving} onClick={() => form.submit()}>
								Save Changes
							</Button>
						</div>
					</div>
				}
			>
				<Spin spinning={loading}>
					<Form layout="vertical" form={form} onFinish={handleSave}>
						<Form.Item name="excludedAssetTypeIds" label="Asset Types">
							<OWAsyncSelect
								mode="multiple"
								stateSlice={assetTypes}
								targetCollectionName={EXCLUDED_ASSET_TYPES_TARGET_COLLECTION}
								fetchMultiple={() => {}}
								fetchData={(
									searchText,
									targetCollectionName,
									pagination,
									sorting,
									filters,
									addToTargetCollection
								) => {
									fetchAssetTypesLite(
										{ name: searchText || undefined, no_pagination: true },
										targetCollectionName,
										pagination,
										sorting,
										filters,
										addToTargetCollection
									);
								}}
								placeholder="Select asset types to exclude"
								renderRecord={(assetType) => (
									<Select.Option key={assetType.id} value={assetType.id}>
										{assetType.name}
									</Select.Option>
								)}
								sortBy={{ sort_by: 'name', order: 'ascend' }}
							/>
						</Form.Item>
					</Form>
				</Spin>
			</Modal>
			{uploadModalVisible ? (
				<UploadWithSampleModal
					type="excluded_asset_types"
					title="Upload Excluded Asset Types"
					downloadText="Download the current list to make updates, then upload the modified file."
					sampleFileText="Download Excluded Asset Types"
					onCancel={handleUploadCancel}
					onDownloadSample={handleDownload}
					downloading={downloading}
					fields={getExcludedAssetTypeFlatfileFields(assetTypeOptions)}
					onDataLoad={handleUpload}
					showWarningMessage={false}
				/>
			) : null}
		</>
	);
};

const mapStateToProps = (state) => ({
	assetTypes: state.asset_types,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	fetchExcludedAssetTypes: () => dispatch(getExcludedAssetTypes(ownProps.userType)),
	saveExcludedAssetTypes: (data) => dispatch(updateExcludedAssetTypes(ownProps.userType)(data)),
	fetchAssetTypesLite: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? assetTypesRestCrudThunksForSupplier.readLite(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
				: assetTypesRestCrudThunksForBuyer.readLite(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
		),
	fetchAllAssetTypesForUpload: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? fetchAssetTypesLiteForSupplier(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
				: fetchAssetTypesLiteForBuyer(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
		),
});

export default withUserType(connect(mapStateToProps, mapDispatchToProps)(ExcludedAssetTypesModal));
